import { InterpolateFunction, InterpolationParameters } from '@ngx-translate/core';
import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";

/**
 * interfaces
 */
import { I18n } from './interfaces';

/**
 * consts
 */
import { ValidationRegExp } from './consts';

/**
 * models
 */
import { AppValidationErrorsModel } from './models';

/**
 * Custom validators class that extends Angular's built-in Validators.
 * Provides internationalized validation error messages using ngx-translate.
 * 
 * @abstract
 * @extends Validators
 * @example
 * ```typescript
 * // Basic usage with i18n support
 * const formControl = new FormControl('', [
 *   AppValidators.required,
 *   AppValidators.email,
 *   AppValidators.minLength(8)
 * ]);
 * 
 * // Password strength validation
 * const passwordControl = new FormControl('', [
 *   AppValidators.required,
 *   AppValidators.minLength(8),
 *   AppValidators.atLeastOneLower,
 *   AppValidators.atLeastOneUpper,
 *   AppValidators.atLeastOneNumber,
 *   AppValidators.atLeastOneSymbol
 * ]);
 * ```
 */
export abstract class AppValidators extends Validators {

    /**
     * Creates an i18n configuration object for validation error messages.
     * 
     * @private
     * @static
     * @param key - The translation key for the error message
     * @param params - Optional interpolation parameters for the translation
     * @returns An object containing i18n configuration with key and params
     */
    public static i18nFn(key: string, params?: InterpolationParameters): I18n {
        return {
            i18n: {
                key,
                params,
            }
        };
    }

    /**
     * Validates that a control has a non-empty value.
     * 
     * @override
     * @static
     * @param control - The form control to validate
     * @returns ValidationErrors object with i18n configuration, or null if valid
     */
    public static override required(control: AbstractControl): ValidationErrors | null {
        
        // ES6 destructuring
        const { required } = super.required(control) || {};
        
        // void validation
        if (!required) return null;

        return new AppValidationErrorsModel('required', true, { key: 'FORM.VALIDATION.REQUIRED' });
        
    }


    /**
     * Creates a custom required validator function with optional custom error message.
     * 
     * @static
     * @param expr - Optional interpolation function or translation key for custom error message
     * @param params - Optional parameters for interpolation
     * @returns A ValidatorFn that validates required fields with custom error messages
     */
    public static requiredFn(expr?: InterpolateFunction | string, params?: InterpolationParameters): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            
            // ES6 destructuring
            const { errorName, i18n } = this.required(control) || {};

            // void validation
            if (!errorName) return null;
            
            return new AppValidationErrorsModel('required', true, { key: expr ?? i18n?.key, params });
        }
    }

    /**
     * Validates that a control contains a valid email address format.
     * 
     * @override
     * @static
     * @param control - The form control to validate
     * @returns ValidationErrors object with i18n configuration, or null if valid
     */
    public static override email(control: AbstractControl): ValidationErrors | null {

        // ES6 destructuring
        const { email } = super.email(control) || {};

        // void validation
        if (!email) return null;

        // return the error
        return new AppValidationErrorsModel('email', true, { key: 'FORM.GENERAL_FIELDS.EMAIL.INVALID' });
    }

    /**
     * Creates a validator function that ensures a control's value meets a minimum length requirement.
     * 
     * @override
     * @static
     * @param length - The minimum required length for the control's value
     * @returns A ValidatorFn that validates minimum length with i18n error message
     */
    public static override minLength(length: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            
            // init validator function
            const _vFn = super.minLength(length);

            // ES6 destructuring
            const { minlength } = _vFn(control) || {};

            // void validation
            if (!minlength) return null;

            return new AppValidationErrorsModel('minlength', true, { key: 'FORM.VALIDATION.MIN_LENGTH', params: { length } });
        }

    }

    /**
     * Creates a validator function that ensures a control's value meets a maximum length requirement.
     * 
     * @override
     * @static
     * @param length - The maximum required length for the control's value
     * @returns A ValidatorFn that validates maximum length with i18n error message
     */
    public static override maxLength(length: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            // init validator function
            const _vFn = super.maxLength(length);

            // ES6 destructuring
            const { maxlength } = _vFn(control) || {};

            // void validation
            if (!maxlength) return null;

            return new AppValidationErrorsModel('maxlength', true, { key: 'FORM.VALIDATION.MAX_LENGTH', params: { length } });
        }

    }

    /**
     * Creates a validator function that ensures a control's numeric value is at least the given minimum.
     *
     * @override
     * @static
     * @param min - The minimum allowed numeric value
     * @returns A ValidatorFn that validates minimum value with i18n error message
     */
    public static override min(min: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            // init validator function
            const _vFn = super.min(min);

            // ES6 destructuring
            const { min: minError } = _vFn(control) || {};

            // void validation
            if (!minError) return null;

            return new AppValidationErrorsModel('min', true, { key: 'FORM.VALIDATION.MIN', params: { min } });
        }
    }

    /**
     * Creates a validator function that ensures a control's numeric value does not exceed the given maximum.
     *
     * @override
     * @static
     * @param max - The maximum allowed numeric value
     * @returns A ValidatorFn that validates maximum value with i18n error message
     */
    public static override max(max: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            // init validator function
            const _vFn = super.max(max);

            // ES6 destructuring
            const { max: maxError } = _vFn(control) || {};

            // void validation
            if (!maxError) return null;

            return new AppValidationErrorsModel('max', true, { key: 'FORM.VALIDATION.MAX', params: { max } });
        }
    }

    /**
     * Validates that a control's value contains at least one lowercase letter.
     * 
     * @static
     * @param control - The form control to validate
     * @returns ValidationErrors object with i18n configuration, or null if valid
     * @example
     * ```typescript
     * const passwordForm = new FormGroup({
     *   password: new FormControl('', [
     *     AppValidators.required,
     *     AppValidators.atLeastOneLower
     *   ])
     * });
     * 
     * // Test cases:
     * passwordForm.get('password')?.setValue('PASSWORD123'); // Invalid - no lowercase
     * passwordForm.get('password')?.setValue('password123'); // Valid - contains lowercase
     * ```
     */
    public static atLeastOneLower(control: AbstractControl): ValidationErrors | null {
        
        // ES6 destructuring
        const { value } = control;

        // void validation
        if (ValidationRegExp.atLeastOneLower(value)) return null;

        // return the error
        return new AppValidationErrorsModel('atLeastOneLower', true, { key: 'FORM.VALIDATION.AT_LEAST_ONE_LOWERCASE_REQUIRED' });

    }

    /**
     * Validates that a control's value contains at least one uppercase letter.
     * 
     * @static
     * @param control - The form control to validate
     * @returns ValidationErrors object with i18n configuration, or null if valid
     * @example
     * ```typescript
     * const passwordForm = new FormGroup({
     *   password: new FormControl('', [
     *     AppValidators.required,
     *     AppValidators.atLeastOneUpper
     *   ])
     * });
     * 
     * // Test cases:
     * passwordForm.get('password')?.setValue('password123'); // Invalid - no uppercase
     * passwordForm.get('password')?.setValue('Password123'); // Valid - contains uppercase
     * ```
     */
    public static atLeastOneUpper(control: AbstractControl): ValidationErrors | null {
        
        // ES6 destructuring
        const { value } = control;
        
        // void validation
        if (ValidationRegExp.atLeastOneUpper(value)) return null;

        // return the error
        return new AppValidationErrorsModel('atLeastOneUpper', true, { key: 'FORM.VALIDATION.AT_LEAST_ONE_UPPERCASE_REQUIRED' });

    }

    /**
     * Validates that a control's value contains at least one numeric digit.
     * 
     * @static
     * @param control - The form control to validate
     * @returns ValidationErrors object with i18n configuration, or null if valid
     * @example
     * ```typescript
     * const passwordForm = new FormGroup({
     *   password: new FormControl('', [
     *     AppValidators.required,
     *     AppValidators.atLeastOneNumber
     *   ])
     * });
     * 
     * // Test cases:
     * passwordForm.get('password')?.setValue('Password'); // Invalid - no numbers
     * passwordForm.get('password')?.setValue('Password1'); // Valid - contains number
     * ```
     */
    public static atLeastOneNumber(control: AbstractControl): ValidationErrors | null {
        
        // ES6 destructuring
        const { value } = control;

        // void validation
        if (ValidationRegExp.atLeastOneNumber(value)) return null;

        // return the error
        return new AppValidationErrorsModel('atLeastOneNumber', true, { key: 'FORM.VALIDATION.AT_LEAST_ONE_NUMBER_REQUIRED' });
        
    }

    /**
     * Validates that a control's value contains at least one special symbol.
     * 
     * @static
     * @param control - The form control to validate
     * @returns ValidationErrors object with i18n configuration, or null if valid
     * @example
     * ```typescript
     * const passwordForm = new FormGroup({
     *   password: new FormControl('', [
     *     AppValidators.required,
     *     AppValidators.atLeastOneSymbol
     *   ])
     * });
     * 
     * // Test cases:
     * passwordForm.get('password')?.setValue('Password123'); // Invalid - no symbols
     * passwordForm.get('password')?.setValue('Password123!'); // Valid - contains symbol
     * ```
     */
    public static atLeastOneSymbol(control: AbstractControl): ValidationErrors | null {
        
        // ES6 destructuring
        const { value } = control;

        // void validation
        if (ValidationRegExp.atLeastOneSymbol(value)) return null;

        // return the error
        return new AppValidationErrorsModel('atLeastOneSymbol', true, { key: 'FORM.VALIDATION.AT_LEAST_ONE_SYMBOL_REQUIRED' });

    }

    /**
     * Group/Array-level validator that surfaces the **first failing child's** errors
     * at the container level, so a parent wrapper (e.g. `app-form-wrapper-field`
     * around an `app-field-group`) can display child validation messages without
     * needing a per-control wrapper.
     *
     * Behavior:
     * - Walks the container's controls in declaration order.
     * - Returns the first child whose `errors` is not `null` as the group's own errors.
     * - If a child is itself a group/array with no own errors but invalid descendants,
     *   recurses into it.
     * - Returns `null` only when every descendant is valid.
     *
     * The returned object is the child's `errors` reference as-is, so the existing
     * i18n shape (`{ errorName, errorValue, i18n }`) and any consumers that read
     * `errors?.['i18n']?.key` continue to work transparently.
     *
     * @static
     * @param control - The `FormGroup` or `FormArray` to evaluate.
     * @returns The first failing child's `ValidationErrors`, or `null` when all are valid.
     * @example
     * ```typescript
     * this._fb.group({
     *   topic:  this._fb.control(null, [AppValidators.required]),
     *   access: this._fb.control(null, [AppValidators.required]),
     * }, { validators: [AppValidators.firstChildError] });
     * ```
     */
    public static firstChildError(control: AbstractControl): ValidationErrors | null {

        // same walk, with nothing ignored
        return AppValidators.firstChildErrorFn()(control);
    }

    /**
     * Configurable variant of {@link AppValidators.firstChildError} that ignores a
     * given set of children while looking for the first failing control. Holds the
     * actual walk — {@link AppValidators.firstChildError} is this with an empty list.
     *
     * Behavior:
     * - Walks the container's controls in declaration order.
     * - Any child whose name matches an entry in `ignore` is skipped entirely —
     *   neither its own errors nor its descendants' errors bubble up.
     * - A child is matched either by its plain name (`'access'`) at any depth, or by
     *   its full dot path from the container the validator is attached to
     *   (`'details.access'`, `'slots.0.from'`) for a more targeted skip.
     * - Non-ignored children still recurse, so deeply-invalid descendants bubble up.
     *
     * The returned object is the child's `errors` reference as-is, so the existing
     * i18n shape (`{ errorName, errorValue, i18n }`) keeps working transparently.
     *
     * @static
     * @param ignore - Control names or dot paths to exclude from the lookup.
     * @returns A ValidatorFn to be applied at the `FormGroup` / `FormArray` level.
     * @example
     * ```typescript
     * this._fb.group({
     *   topic:  this._fb.control(null, [AppValidators.required]),
     *   access: this._fb.control(null, [AppValidators.required]),
     *   note:   this._fb.control(null, [AppValidators.required]),
     * }, { validators: [AppValidators.firstChildErrorFn(['note'])] });
     * // `note` errors never surface on the group — only `topic` / `access` do.
     * ```
     */
    public static firstChildErrorFn(ignore: string[] = []): ValidatorFn {

        // normalize once so lookups stay O(1) per child
        const _ignored = new Set(ignore);

        const _walk = (control: AbstractControl, path: string): ValidationErrors | null => {

            // ES6 destructuring of the container's children collection
            const { controls } = (control as FormGroup | FormArray) ?? {};

            // void validation — not a group/array
            if (!controls) return null;

            // normalize FormGroup (object) and FormArray (array) into name/control pairs
            const _children: [string, AbstractControl][] = Array.isArray(controls)
                ? controls.map((_child, _index) => [String(_index), _child])
                : Object.entries(controls);

            // walk children in order; first non-ignored one with errors wins
            for (const [_name, _child] of _children) {

                // full dot path from the container the validator is attached to
                const _path = path ? `${path}.${_name}` : _name;

                // skip by plain name (any depth) or by full path
                if (_ignored.has(_name) || _ignored.has(_path)) continue;

                if (_child?.errors) return _child.errors;

                // recurse into nested groups/arrays so deeply-invalid descendants still bubble up
                if (_child && 'controls' in _child) {
                    const _nested = _walk(_child, _path);
                    if (_nested) return _nested;
                }
            }

            return null;
        };

        return (control: AbstractControl): ValidationErrors | null => _walk(control, '');
    }

    /**
     * FormGroup-level validator that enforces a numeric min control value is strictly
     * less than a max control value (comparison by value, not length).
     *
     * @static
     * @param minControlName - Name of the min control (default: 'min')
     * @param maxControlName - Name of the max control (default: 'max')
     * @returns A ValidatorFn to be applied at the FormGroup level
     * @example
     * ```typescript
     * this._form = this._fb.group({
     *   minPrice: this._fb.control(null),
     *   maxPrice: this._fb.control(null),
     * }, {
     *   validators: AppValidators.minMaxRange('minPrice', 'maxPrice')
     * });
     * ```
     */
    public static minMaxRange(minControlName: string, maxControlName: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            // ES6 destructuring
            const { [minControlName]: minControl, [maxControlName]: maxControl } = (control as FormGroup).controls ?? {};

            // values
            const minVal = minControl?.value;
            const maxVal = maxControl?.value;

            // void validation
            if (minVal == null || minVal == '' || maxVal == null || maxVal == '') {
                return null;
            }

            if (Number(minVal) >= Number(maxVal)) {
                minControl.setErrors(new AppValidationErrorsModel('minLessThanMax', true, { key: 'FORM.VALIDATION.MIN_LESS_THAN_MAX' }));
                maxControl.setErrors(new AppValidationErrorsModel('maxGreaterThanMin', true, { key: 'FORM.VALIDATION.MAX_GREATER_THAN_MIN' }));
            }
            else {
                minControl.setErrors(null);
                maxControl.setErrors(null);
            }

            return null;
        };
    }

}
